import { Trans, useLingui } from '@lingui/react/macro'
import { Checkbox } from 'designSystem/components/Checkbox'
import {
  KNOB_HELP,
  METHOD_HELP,
  METHOD_LABELS,
} from 'features/timeSeries/experiment/knobCopy'
import {
  KnobCheckRow,
  KnobField,
  KnobGrid,
  KnobGroupTitle,
  KnobHint,
  KnobInput,
  KnobPanel,
  KnobRow,
  KnobSelect,
} from 'features/timeSeries/experiment/knobFields'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import type { KnobNoteRenderer } from 'features/timeSeries/experiment/SamplingKnobs'
import type {
  BandEstimator,
  ExperimentConfig,
  ExperimentMethodKey,
  FeatureKnobs,
} from 'features/timeSeries/experiment/types'
import { EXPERIMENT_METHOD_KEYS } from 'features/timeSeries/experiment/types'
import type { LabPush } from 'features/timeSeries/experiment/useLabTransformsOnChart'
import { FC, Fragment, useState } from 'react'

const parsePositiveList = (text: string): number[] =>
  text
    .split(/[,\s]+/)
    .map((part) => Number(part))
    .filter((value) => Number.isFinite(value) && value > 0)

export interface ModelKnobsProps {
  config: ExperimentConfig
  onPatch: (patch: Partial<ExperimentConfig>) => void
  onFeaturePatch: (patch: Partial<FeatureKnobs>) => void
  onToggleMethod: (method: ExperimentMethodKey) => void
  noteFor?: KnobNoteRenderer
  /**
   * Raw text for the comma-separated list inputs. Supply these (with their
   * setters) to keep a half-typed "1,3," across remounts, as the Experiment Lab
   * does; omit them and the buffers live inside this component.
   */
  lagsText?: string
  onLagsTextChange?: (text: string) => void
  hiddenText?: string
  onHiddenTextChange?: (text: string) => void
  /**
   * Draws the rolling-feature window on the chart's pens. Supplied where a
   * chart is on screen to draw on; omitted elsewhere, and the toggle is then
   * absent.
   */
  chartRollingFeature?: LabPush
}

/**
 * The feature and model knob panels: driver history, MLP shape, kNN, the
 * residual band, seed, and the row-count floors.
 *
 * Extracted from ExperimentTab so the agentic sentinel-design plan review edits
 * the plan through exactly the same controls (and help copy) as the lab —
 * everything it needs arrives as props, with no lab context.
 */
export const ModelKnobs: FC<ModelKnobsProps> = ({
  config,
  onPatch,
  onFeaturePatch,
  onToggleMethod,
  noteFor,
  lagsText,
  onLagsTextChange,
  hiddenText,
  onHiddenTextChange,
  chartRollingFeature,
}) => {
  const { i18n, t } = useLingui()

  const [ownLagsText, setOwnLagsText] = useState(() =>
    (config.feature.driver_lags || []).join(',')
  )
  const [ownHiddenText, setOwnHiddenText] = useState(() =>
    (config.mlp_hidden_layer_sizes || []).join(',')
  )
  const lags = lagsText ?? ownLagsText
  const setLags = onLagsTextChange ?? setOwnLagsText
  const hidden = hiddenText ?? ownHiddenText
  const setHidden = onHiddenTextChange ?? setOwnHiddenText

  const note = (field: string) => <Fragment>{noteFor?.(field)}</Fragment>

  return (
    <KnobGrid>
      <KnobPanel>
        <KnobGroupTitle>
          <Trans>Features</Trans>
        </KnobGroupTitle>
        <KnobField>
          <KnobHelp
            label={t`Driver lags (comma-separated periods)`}
            help={KNOB_HELP.driverLags}
          />
          <KnobInput
            value={lags}
            onChange={(e) => {
              setLags(e.target.value)
              const parsed = parsePositiveList(e.target.value)
              if (parsed.length) onFeaturePatch({ driver_lags: parsed })
            }}
          />
          {note('feature.driver_lags')}
        </KnobField>
        <KnobField>
          <KnobHelp label={t`Rolling window`} help={KNOB_HELP.rollingWindow} />
          <KnobInput
            type='number'
            min={1}
            value={config.feature.rolling_window}
            onChange={(e) =>
              onFeaturePatch({ rolling_window: Number(e.target.value) || 6 })
            }
          />
          {note('feature.rolling_window')}
        </KnobField>
        {chartRollingFeature && (
          <>
            <KnobCheckRow>
              <Checkbox
                checked={chartRollingFeature.active}
                disabled={chartRollingFeature.unavailableReason !== null}
                onCheckedChange={(v) =>
                  chartRollingFeature.setActive(v === true)
                }
              />
              <Trans>Show this window on the chart</Trans>
            </KnobCheckRow>
            {chartRollingFeature.unavailableReason !== null ? (
              <KnobHint>{chartRollingFeature.unavailableReason}</KnobHint>
            ) : (
              chartRollingFeature.active && (
                <KnobHint>
                  <Trans>
                    Drivers trail the current period; the target is shifted one
                    period so its own value stays out, as the features are
                    built. Replaces a denoise overlay on the same pen.
                  </Trans>
                </KnobHint>
              )
            )}
          </>
        )}
        <KnobField>
          <KnobHelp
            label={t`Driver ffill limit`}
            help={KNOB_HELP.driverFfill}
          />
          <KnobInput
            type='number'
            min={0}
            value={config.feature.driver_ffill_limit}
            onChange={(e) =>
              onFeaturePatch({
                driver_ffill_limit: Number(e.target.value) || 0,
              })
            }
          />
          {note('feature.driver_ffill_limit')}
        </KnobField>
        <KnobCheckRow>
          <Checkbox
            checked={config.feature.include_driver_history}
            onCheckedChange={(v) =>
              onFeaturePatch({ include_driver_history: !!v })
            }
          />
          <KnobHelp
            label={t`Driver history (lags + rolling)`}
            help={KNOB_HELP.driverHistory}
          />
        </KnobCheckRow>
        {note('feature.include_driver_history')}
        <KnobCheckRow>
          <Checkbox
            checked={config.feature.include_self_history}
            onCheckedChange={(v) =>
              onFeaturePatch({ include_self_history: !!v })
            }
          />
          <KnobHelp
            label={t`Self history (target lags)`}
            help={KNOB_HELP.selfHistory}
          />
        </KnobCheckRow>
        {note('feature.include_self_history')}
      </KnobPanel>

      <KnobPanel>
        <KnobGroupTitle>
          <Trans>Model</Trans>
        </KnobGroupTitle>
        {EXPERIMENT_METHOD_KEYS.map((key) => (
          <KnobCheckRow key={key}>
            <Checkbox
              checked={config.methods.includes(key)}
              onCheckedChange={() => onToggleMethod(key)}
            />
            <KnobHelp
              label={
                METHOD_LABELS[key]
                  ? i18n._(METHOD_LABELS[key])
                  : key.replace('target_', '')
              }
              help={METHOD_HELP[key] ?? ''}
            />
          </KnobCheckRow>
        ))}
        {note('methods')}
        <KnobField>
          <KnobHelp label={t`MLP hidden sizes`} help={KNOB_HELP.mlpHidden} />
          <KnobInput
            value={hidden}
            onChange={(e) => {
              setHidden(e.target.value)
              const parsed = parsePositiveList(e.target.value)
              if (parsed.length) onPatch({ mlp_hidden_layer_sizes: parsed })
            }}
          />
          {note('mlp_hidden_layer_sizes')}
        </KnobField>
        <KnobField>
          <KnobHelp label={t`MLP max iter`} help={KNOB_HELP.mlpMaxIter} />
          <KnobInput
            type='number'
            value={config.mlp_max_iter}
            onChange={(e) =>
              onPatch({ mlp_max_iter: Number(e.target.value) || 300 })
            }
          />
          {note('mlp_max_iter')}
        </KnobField>
        <KnobField>
          <KnobHelp label={t`kNN k`} help={KNOB_HELP.knnK} />
          <KnobInput
            type='number'
            min={1}
            value={config.knn_k}
            onChange={(e) => onPatch({ knn_k: Number(e.target.value) || 25 })}
          />
          {note('knn_k')}
        </KnobField>
        <KnobField>
          <KnobHelp label={t`kNN library cap`} help={KNOB_HELP.knnFitCap} />
          <KnobInput
            type='number'
            min={0}
            value={config.knn_fit_cap}
            onChange={(e) =>
              onPatch({ knn_fit_cap: Math.max(0, Number(e.target.value) || 0) })
            }
          />
          {note('knn_fit_cap')}
        </KnobField>
        <KnobCheckRow>
          <Checkbox
            checked={config.score_complete_rows_only}
            onCheckedChange={(v) => onPatch({ score_complete_rows_only: !!v })}
          />
          <KnobHelp
            label={t`Score complete rows only`}
            help={KNOB_HELP.scoreCompleteRowsOnly}
          />
        </KnobCheckRow>
        {note('score_complete_rows_only')}
        <KnobField>
          <KnobHelp label={t`Band estimator`} help={KNOB_HELP.bandEstimator} />
          <KnobSelect
            value={config.band_estimator}
            onChange={(e) =>
              onPatch({ band_estimator: e.target.value as BandEstimator })
            }>
            <option value='iqr'>
              <Trans>IQR (prod)</Trans>
            </option>
            <option value='std'>std</option>
            <option value='abs_quantile'>
              <Trans>abs quantile</Trans>
            </option>
          </KnobSelect>
          {note('band_estimator')}
        </KnobField>
        {config.band_estimator === 'abs_quantile' && (
          <KnobField>
            <KnobHelp label={t`Band quantile`} help={KNOB_HELP.bandQuantile} />
            <KnobInput
              type='number'
              min={0.5}
              max={0.99}
              step={0.01}
              value={config.band_quantile}
              onChange={(e) =>
                onPatch({ band_quantile: Number(e.target.value) || 0.9 })
              }
            />
            {note('band_quantile')}
          </KnobField>
        )}
        <KnobField>
          <KnobHelp label={t`Band k (multiplier)`} help={KNOB_HELP.bandK} />
          <KnobInput
            type='number'
            min={0.5}
            step={0.1}
            value={config.residual_band_k}
            onChange={(e) =>
              onPatch({ residual_band_k: Number(e.target.value) || 2 })
            }
          />
          {note('residual_band_k')}
        </KnobField>
        <KnobField>
          <KnobHelp label={t`Random seed`} help={KNOB_HELP.randomSeed} />
          <KnobInput
            type='number'
            value={config.random_state}
            onChange={(e) =>
              onPatch({ random_state: Number(e.target.value) || 17 })
            }
          />
          {note('random_state')}
        </KnobField>
        <KnobField>
          <KnobHelp
            label={t`Min usable / train rows`}
            help={KNOB_HELP.minRows}
          />
          <KnobRow>
            <KnobInput
              type='number'
              value={config.min_usable_rows}
              onChange={(e) =>
                onPatch({ min_usable_rows: Number(e.target.value) || 200 })
              }
            />
            <KnobInput
              type='number'
              value={config.min_train_rows}
              onChange={(e) =>
                onPatch({ min_train_rows: Number(e.target.value) || 100 })
              }
            />
          </KnobRow>
          {note('min_usable_rows')}
        </KnobField>
      </KnobPanel>
    </KnobGrid>
  )
}
